import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Scan, Image as ImgIcon, AlertTriangle } from "lucide-react";
import { inventoryApi } from "../lib/shop-api";
import { money } from "../lib/api";

export default function AdminProducts() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [stockLevel, setStockLevel] = useState("all");
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { inventoryApi.categories().then(setCats).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      inventoryApi.listProducts({ q, category: cat, stock_level: stockLevel })
        .then(setProducts)
        .finally(() => setLoading(false));
    }, 150);
    return () => clearTimeout(t);
  }, [q, cat, stockLevel]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">Catálogo</p>
          <h1 className="text-4xl text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }}>Productos</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/scan" className="flex items-center gap-2 bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] font-bold uppercase tracking-wider px-4 py-2.5 rounded-full text-sm" data-testid="products-scan-btn">
            <Scan size={16} /> Scan
          </Link>
          <Link to="/admin/products/new" className="flex items-center gap-2 bg-white/5 border border-white/15 text-[#F5F0E6] px-4 py-2.5 rounded-full text-sm hover:border-[#C89B3C]" data-testid="products-new-btn">
            <Plus size={16} /> Nuevo producto
          </Link>
        </div>
      </div>

      <div className="bg-[#120F0D] border border-white/10 rounded-xl p-4 mb-5 grid md:grid-cols-[1fr_200px_200px] gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F5F0E6]/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, SKU, barcode, marca…" className="w-full bg-[#0A0908] border border-white/15 rounded px-9 py-2 text-sm text-[#F5F0E6]" data-testid="products-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="products-category-filter">
          <option value="">Todas las categorías</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={stockLevel} onChange={(e) => setStockLevel(e.target.value)} className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="products-stock-filter">
          <option value="all">Todos</option>
          <option value="in_stock">En stock</option>
          <option value="low">Low stock</option>
          <option value="out">Agotados</option>
        </select>
      </div>

      <div className="bg-[#120F0D] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <p className="p-6 text-[#F5F0E6]/60">Cargando…</p>
        ) : products.length === 0 ? (
          <p className="p-6 text-[#F5F0E6]/60">Sin resultados.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#F5F0E6]/50 border-b border-white/10">
                <th className="p-3">Producto</th>
                <th className="p-3">SKU / Barcode</th>
                <th className="p-3">Categoría</th>
                <th className="p-3">Precio</th>
                <th className="p-3 text-right">Stock</th>
              </tr>
            </thead>
            <tbody data-testid="products-table-body">
              {products.map((p) => {
                const low = p.total_stock > 0 && p.total_stock <= (p.low_stock_threshold || 5);
                const out = p.total_stock === 0;
                return (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <Link to={`/admin/products/${p.id}`} className="flex items-center gap-3 group" data-testid={`product-row-${p.id}`}>
                        <div className="w-10 h-10 bg-black/40 rounded overflow-hidden shrink-0">
                          {(p.images || [])[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><ImgIcon size={16} /></div>}
                        </div>
                        <div>
                          <p className="text-[#F5F0E6] group-hover:text-[#C89B3C]">{p.name}</p>
                          <p className="text-xs text-[#F5F0E6]/50">{p.brand}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="p-3 font-mono text-xs text-[#F5F0E6]/70">
                      <div>{p.sku || "—"}</div>
                      <div className="text-[#F5F0E6]/50">{p.barcode || "—"}</div>
                    </td>
                    <td className="p-3 text-[#F5F0E6]/80">{p.category}</td>
                    <td className="p-3 text-[#F5F0E6]">{money(p.price)}</td>
                    <td className="p-3 text-right">
                      <span className={`inline-flex items-center gap-1 font-bold ${out ? "text-red-400" : low ? "text-amber-400" : "text-green-400"}`}>
                        {(low || out) && <AlertTriangle size={12} />}
                        {p.total_stock}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
